import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './CollegeFantasy.css';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatEt = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
  }) + ' ET';
};

const formatGameDay = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York',
  });
};

const teamName = (teams, id) => teams.find((team) => String(team.id) === String(id))?.name || 'Team';

const CollegeFantasy = () => {
  const [hub, setHub] = useState(null);
  const [seasonPayload, setSeasonPayload] = useState(null);
  const [entry, setEntry] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [recaps, setRecaps] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedGameIds, setSelectedGameIds] = useState([]);
  const [teamQuery, setTeamQuery] = useState('');
  const [teamListOpen, setTeamListOpen] = useState(true);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const signedIn = !!localStorage.getItem('token');
  const gamesSectionRef = useRef(null);

  const cfb = hub?.sports?.cfb || null;
  const playable = cfb && cfb.status !== 'draft';

  const loadHub = useCallback(async () => {
    const res = await axios.get(`${apiUrl}/api/fantasy/hub`);
    setHub(res.data);
    return res.data;
  }, []);

  const loadSeason = useCallback(async (seasonId) => {
    const [seasonRes, boardRes, recapRes] = await Promise.all([
      axios.get(`${apiUrl}/api/fantasy/seasons/${seasonId}`),
      axios.get(`${apiUrl}/api/fantasy/seasons/${seasonId}/leaderboard`),
      axios.get(`${apiUrl}/api/fantasy/seasons/${seasonId}/recaps`),
    ]);
    setSeasonPayload(seasonRes.data);
    setLeaderboard(boardRes.data || []);
    setRecaps(recapRes.data || []);
    if (signedIn) {
      const entryRes = await axios.get(`${apiUrl}/api/fantasy/seasons/${seasonId}/entry`, {
        headers: authHeaders(),
      });
      if (entryRes.data) {
        setEntry(entryRes.data);
        setSelectedTeamId(entryRes.data.selectedTeamId);
        setSelectedGameIds((entryRes.data.selectedGameIds || []).map(String));
        if (entryRes.data.selectedTeamId) setTeamListOpen(false);
      }
    }
  }, [signedIn]);

  useEffect(() => {
    loadHub()
      .then((data) => {
        const season = data?.sports?.cfb;
        if (season && season.status !== 'draft') {
          return loadSeason(season.id);
        }
        return null;
      })
      .catch(() => setMessage('Could not load College Fantasy right now.'));
  }, [loadHub, loadSeason]);

  const teams = seasonPayload?.teams || [];
  const games = seasonPayload?.games || [];
  const season = seasonPayload?.season;
  const lockDate = season?.lockDate || cfb?.lockDate;
  const locked = Boolean(
    season?.locked
    || cfb?.status === 'locked'
    || cfb?.status === 'final'
    || (lockDate && new Date() >= new Date(lockDate))
  );
  const inSeason = Boolean(playable && locked);
  const showPicks = Boolean(
    locked || leaderboard.some((row) => Array.isArray(row.pickedGames))
  );

  const teamGames = useMemo(() => {
    if (!selectedTeamId) return [];
    return games
      .filter((game) => String(game.homeTeamId) === String(selectedTeamId) || String(game.awayTeamId) === String(selectedTeamId))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [games, selectedTeamId]);

  const filteredTeams = useMemo(() => {
    const query = teamQuery.trim().toLowerCase();
    return teams.filter((team) => !query || team.name.toLowerCase().includes(query));
  }, [teams, teamQuery]);

  const toggleGame = (gameId) => {
    if (locked) return;
    const id = String(gameId);
    setSelectedGameIds((current) => {
      if (current.includes(id)) return current.filter((item) => item !== id);
      if (current.length >= 4) return current;
      return [...current, id];
    });
  };

  const scrollToGames = () => {
    window.requestAnimationFrame(() => {
      if (gamesSectionRef.current) {
        gamesSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  const chooseTeam = (teamId) => {
    if (locked) return;
    const sameTeam = String(selectedTeamId) === String(teamId);
    setSelectedTeamId(teamId);
    if (!sameTeam) setSelectedGameIds([]);
    setTeamListOpen(false);
    setTeamQuery('');
    scrollToGames();
  };

  const saveEntry = async () => {
    if (!season) return;
    setSaving(true);
    setMessage('');
    try {
      await axios.put(
        `${apiUrl}/api/fantasy/seasons/${season.id}/entry`,
        { selectedTeamId, selectedGameIds },
        { headers: authHeaders() }
      );
      setMessage('Your four playoff games are saved.');
      await loadSeason(season.id);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Could not save your picks.');
    } finally {
      setSaving(false);
    }
  };

  const renderRankAlert = () => (
    entry?.rank && entry.previousRank && entry.rank !== entry.previousRank ? (
      <p className="fantasy-banner fantasy-alert">
        Leaderboard alert: you moved from #{entry.previousRank} to #{entry.rank}.
      </p>
    ) : null
  );

  const renderLeaderboard = () => (
    <section className="fantasy-section">
      <h2>Current leaderboard</h2>
      {showPicks && (
        <p>Picks are public now that the lock date has passed. Everyone can see each player's four games.</p>
      )}
      <div className="fantasy-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Rank</th>
              <th>Player</th>
              <th>Team</th>
              {showPicks && <th>Games picked</th>}
              <th>Record</th>
              <th>SOS</th>
              <th>2nd SOS</th>
              <th>Move</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((row) => {
              const move = row.previousRank && row.rank ? row.previousRank - row.rank : 0;
              return (
                <tr key={`${row.userName}-${row.rank}`}>
                  <td>{row.rank || '—'}</td>
                  <td>{row.userName}</td>
                  <td>{row.teamName}</td>
                  {showPicks && (
                    <td>
                      {Array.isArray(row.pickedGames) && row.pickedGames.length ? (
                        <ul className="picked-games">
                          {row.pickedGames.map((game) => (
                            <li key={game.id}>
                              <span className="pick-matchup">{game.label}</span>
                              <span className="pick-date">{formatGameDay(game.date)}</span>
                              {game.result && (
                                <span className={`pick-result pick-${game.result.toLowerCase()}`}>
                                  {game.result}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        '—'
                      )}
                    </td>
                  )}
                  <td>{row.wins}-{row.losses}</td>
                  <td>{row.sosPoints}</td>
                  <td>{row.secondSosPoints}</td>
                  <td>
                    {move > 0 && <span className="rank-up">↑{move}</span>}
                    {move < 0 && <span className="rank-down">↓{Math.abs(move)}</span>}
                    {move === 0 && '—'}
                  </td>
                </tr>
              );
            })}
            {!leaderboard.length && (
              <tr><td colSpan={showPicks ? 8 : 7}>No entries yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );

  const renderRecaps = () => (
    <section className="fantasy-section">
      <h2>End-of-week recaps</h2>
      {recaps.length ? recaps.map((recap) => (
        <article key={recap._id} className="recap-card">
          <strong>{recap.weekLabel}</strong>
          <p>{recap.text}</p>
          {!!recap.movements?.length && (
            <p>
              {recap.movements.slice(0, 8).map((item) => (
                `${item.userName} ${item.previousRank}→${item.rank}`
              )).join(' · ')}
            </p>
          )}
        </article>
      )) : <p>No recaps yet. Check back after the first calculate.</p>}
    </section>
  );

  const renderPickSections = () => (
    <>
      <section className="fantasy-section">
        <h2>How it is played</h2>
        <p>Record comes first (4-0 through 0-4). Then strength of schedule from Top 25 opponents. Then second-level SOS from how many ranked teams those opponents played.</p>
        <p>Picks lock at {formatEt(season.lockDate)}. After that, your four games stay put.</p>
      </section>

      <section className="fantasy-section">
        <h2>1. Choose your team</h2>
        {selectedTeamId && (
          <div className="chosen-team-bar">
            <div>
              <p className="chosen-team-label">Playing as</p>
              <strong>{teamName(teams, selectedTeamId)}</strong>
            </div>
            <button
              type="button"
              className="fantasy-secondary"
              onClick={() => setTeamListOpen((open) => !open)}
              disabled={locked}
            >
              {teamListOpen ? 'Hide team list' : 'Change team'}
            </button>
          </div>
        )}
        {(!selectedTeamId || teamListOpen) && (
          <>
            <p>Pick one school. After that, the list tucks away so you can choose four playoff games.</p>
            <input
              value={teamQuery}
              onChange={(e) => setTeamQuery(e.target.value)}
              placeholder="Search college football teams"
              disabled={locked}
            />
            <div className="fantasy-grid">
              {filteredTeams.map((team) => (
                <button
                  key={team.id}
                  type="button"
                  className={`game-card ${String(selectedTeamId) === String(team.id) ? 'is-selected' : ''}`}
                  onClick={() => chooseTeam(team.id)}
                  disabled={locked}
                >
                  <strong>{team.name}</strong>
                </button>
              ))}
            </div>
          </>
        )}
      </section>

      {selectedTeamId && (
        <section className="fantasy-section" ref={gamesSectionRef}>
          <h2>2. Tap 4 of these {teamGames.length || 12} game cards into your playoff</h2>
          <p className="fantasy-next-step">
            Next step: tap a card to add it, tap again to remove it.
            Fill all 4 slots below. You have {selectedGameIds.length} of 4 selected.
          </p>
          <h3>Your selected playoff games</h3>
          <div className="selected-board">
            {[0, 1, 2, 3].map((index) => {
              const game = teamGames.find((item) => String(item.id) === selectedGameIds[index]);
              if (!game) return <div key={index} className="selected-empty">Empty slot {index + 1}</div>;
              return renderGameCard(game, true);
            })}
          </div>
          {!signedIn ? (
            <Link to="/sign-on">Sign on to enter your playoff slate.</Link>
          ) : (
            <div className="fantasy-actions">
              <button type="button" onClick={saveEntry} disabled={saving || locked || selectedGameIds.length !== 4}>
                {locked ? 'Picks are locked' : selectedGameIds.length === 4 ? 'Save my four games' : `Pick ${4 - selectedGameIds.length} more game${4 - selectedGameIds.length === 1 ? '' : 's'}`}
              </button>
            </div>
          )}
          <h3>This team’s schedule</h3>
          <div className="fantasy-grid">
            {teamGames.map((game) => renderGameCard(game, selectedGameIds.includes(String(game.id))))}
          </div>
        </section>
      )}
    </>
  );

  const renderGameCard = (game, selected) => {
    const opponentId = String(game.homeTeamId) === String(selectedTeamId) ? game.awayTeamId : game.homeTeamId;
    const atHome = String(game.homeTeamId) === String(selectedTeamId);
    const result = game.winnerTeamId
      ? (String(game.winnerTeamId) === String(selectedTeamId) ? 'Win' : 'Loss')
      : 'Not played';
    return (
      <button
        key={game.id}
        type="button"
        className={`game-card ${selected ? 'is-selected' : ''}`}
        onClick={() => toggleGame(game.id)}
        disabled={locked}
      >
        <div className="game-date">{formatEt(game.date)}</div>
        <strong>{atHome ? 'vs' : '@'} {teamName(teams, opponentId)}</strong>
        <div>{atHome ? 'Home' : 'Away'} · {result}</div>
        <div>{selected ? 'In your playoff' : 'Tap to select'}</div>
      </button>
    );
  };

  const seasonYear = season?.year || cfb?.year;

  return (
    <div className="fantasy-page">
      <header className="fantasy-hero">
        <h1>College Fantasy</h1>
        {inSeason ? (
          <p className="fantasy-lead">
            The {seasonYear} College Football Fantasy Playoff has started and is now closed to new players, since the season is underway.
            You can watch and follow the action on this page throughout college football season.
          </p>
        ) : (
          <p className="fantasy-lead">
            Pick a team, choose four real games, and ride that playoff slate against everyone else.
            Seasons only open after an admin loads the year, dates, teams, and schedule.
          </p>
        )}
      </header>

      {message && <p className="fantasy-banner">{message}</p>}

      {!inSeason && (
        <section className="fantasy-sports">
          <article className="fantasy-sport-card">
            <h2>College Football Fantasy Playoff</h2>
            {playable ? (
              <p>{cfb.year} season is {cfb.status}. Lock: {formatEt(cfb.lockDate)}</p>
            ) : (
              <p>Season not yet configured.</p>
            )}
          </article>
        </section>
      )}

      {!playable && (
        <p className="fantasy-banner">Season not yet configured. An admin will post the year, dates, teams, and schedule before play starts.</p>
      )}

      {playable && season && (
        inSeason ? (
          <>
            {renderRankAlert()}
            {renderRecaps()}
            {renderLeaderboard()}
            {renderPickSections()}
          </>
        ) : (
          <>
            {renderRankAlert()}
            {renderPickSections()}
            {renderLeaderboard()}
            {renderRecaps()}
          </>
        )
      )}
    </div>
  );
};

export default CollegeFantasy;
